import React, { useEffect, useState } from "react";
import EditGeneral from "../Components/Location/EditGeneral";
import EditScheduling from "../Components/Location/EditScheduling";
import EditTimesheet from "../Components/Location/EditTimesheet";
import { useNavigate, useParams } from "react-router-dom";
import api from "../Utils/api";
import { toast } from "react-toastify";
import Spinner from "../Components/Spinner";

const defaultOperatingHour = {
  enable: true,
  startTime: "09:00",
  endTime: "17:00",
};
const daysOfWeek = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
];

export default function LocationManagementPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState("general");

  const [isLoading, setIsLoading] = useState(false);

  const [areas, setAreas] = useState([
    { id: 1, name: "Area00" },
    { id: 2, name: "Guard" },
  ]);

  const [newAreaName, setNewAreaName] = useState("");

  const [schedulingSettings, setSchedulingSettings] = useState({
    firstDayOfWeek: "Mon",
    defaultShiftDuration: 8,
    defaultBreakDuration: 30,
    allowToClaimOrRequestOpenShifts: false,
    displayLocationAndAreas: false,
    swapShifts: false,
    managerApprovalForShiftSwaps: false,
    offerShifts: false,
    overtimePremiumCost: 0,
    defaultOvertimeShiftCost: 0,
  });

  const [timesheetSettings, setTimesheetSettings] = useState({
    clockInOutViaWeb: false,
    clockInOutViaMobile: false,
    captureGpsOnClockViaMobile: false,
    blockClockingFromOutsideDistance: false,
    clockInOutViaSms: false,
    requirePhotoWhenStartAndEnd: false,
    maximumTimesheetLength: 15,

    // Timesheet Approving
    autoTimeApproveTimesheets: "never",
    automaticallyRound: false,
    roundToScheduleStartIfEarlier: false,
    startTimeRounding: 15,
    endTimeRounding: 15,
    breakTimeRounding: 15,

    // Team Member Permission
    addAndEditTimesheets: false,
    modifyTimesheetsDuringshifts: false,
    startShiftsAnytimeBeforeScheduledTime: false,
    howEarlyTeamMembersCanStartShifts: 5,
    endBreakAnyTime: false,

    // Unscheduled Shifts
    linkUnscheduledTimesheetWith: "best_fit",
    timeVariance: "no_preference",
    requiredUnscheduledTimesheet: false,

    // Breaks
    addScheduledBreaksWhenClockingOut: false,
    displayWarningForShorterOrMissedBreaks: false,
    paidMealBreaks: "unpaid",
  });

  const [generalSettings, setGeneralSettings] = useState({
    name: "",
    area: "",
    color: "",
    address1: "",
    address2: "",
    city: "",
    zipCode: "",
    country: "",
    timezone: "",
    text: "",
    attachmentUrls: [],
    ...Object.fromEntries(
      daysOfWeek.map((day) => [day, { ...defaultOperatingHour }])
    ),
  });

  const addArea = () => {
    if (newAreaName.trim()) {
      setAreas((prev) => [
        ...prev,
        { id: Date.now(), name: newAreaName.trim() },
      ]);
      setNewAreaName("");
    }
  };

  const deleteArea = (id) => {
    setAreas((prev) => prev.filter((area) => area.id !== id));
  };

  const tabs = [
    { id: "general", label: "General" },
    // { id: "areas", label: "Areas" },
    { id: "scheduling", label: "Scheduling" },
    { id: "timesheets", label: "Timesheets" },
  ];

  const getLocationSettingsInfo = async () => {
    setIsLoading(true);
    const res = await api.get(`/location/${id}`);
    setIsLoading(false);
    if (res.data.success) {
      setSchedulingSettings({
        firstDayOfWeek: res.data.location.firstDayOfWeek,
        defaultShiftDuration: res.data.location.defaultShiftDuration,
        defaultBreakDuration: res.data.location.defaultBreakDuration,
        allowToClaimOrRequestOpenShifts:
          res.data.location.allowToClaimOrRequestOpenShifts,
        displayLocationAndAreas: res.data.location.displayLocationAndAreas,
        swapShifts: res.data.location.swapShifts,
        managerApprovalForShiftSwaps:
          res.data.location.managerApprovalForShiftSwaps,
        offerShifts: res.data.location.offerShifts,
        overtimePremiumCost: res.data.location.overtimePremiumCost,
        defaultOvertimeShiftCost: res.data.location.defaultOvertimeShiftCost,
      });

      setTimesheetSettings({
        clockInOutViaWeb: res.data.location.clockInOutViaWeb,
        clockInOutViaMobile: res.data.location.clockInOutViaMobile,
        captureGpsOnClockViaMobile:
          res.data.location.captureGpsOnClockViaMobile,
        blockClockingFromOutsideDistance:
          res.data.location.blockClockingFromOutsideDistance,
        clockInOutViaSms: res.data.location.clockInOutViaSms,
        requirePhotoWhenStartAndEnd:
          res.data.location.requirePhotoWhenStartAndEnd,
        maximumTimesheetLength: res.data.location.maximumTimesheetLength,

        autoTimeApproveTimesheets: res.data.location.autoTimeApproveTimesheets,
        automaticallyRound: res.data.location.automaticallyRound,
        roundToScheduleStartIfEarlier:
          res.data.location.roundToScheduleStartIfEarlier,
        startTimeRounding: res.data.location.startTimeRounding,
        endTimeRounding: res.data.location.endTimeRounding,
        breakTimeRounding: res.data.location.breakTimeRounding,

        addAndEditTimesheets: res.data.location.addAndEditTimesheets,
        modifyTimesheetsDuringshifts:
          res.data.location.modifyTimesheetsDuringshifts,
        startShiftsAnytimeBeforeScheduledTime:
          res.data.location.startShiftsAnytimeBeforeScheduledTime,
        howEarlyTeamMembersCanStartShifts:
          res.data.location.howEarlyTeamMembersCanStartShifts,
        endBreakAnyTime: res.data.location.endBreakAnyTime,

        linkUnscheduledTimesheetWith:
          res.data.location.linkUnscheduledTimesheetWith,
        timeVariance: res.data.location.timeVariance,
        requiredUnscheduledTimesheet:
          res.data.location.requiredUnscheduledTimesheet,

        addScheduledBreaksWhenClockingOut:
          res.data.location.addScheduledBreaksWhenClockingOut,
        displayWarningForShorterOrMissedBreaks:
          res.data.location.displayWarningForShorterOrMissedBreaks,
        paidMealBreaks: res.data.location.paidMealBreaks,
      });

      setGeneralSettings({
        name: res.data.location.name,
        area: res.data.location.area,
        address1: res.data.location.address1,
        address2: res.data.location.address2,
        city: res.data.location.city,
        color: res.data.location.color,
        zipCode: res.data.location.zipCode,
        country: res.data.location.country,
        timezone: res.data.location.timezone,
        text: res.data.location.text,
        attachmentUrls: res.data.location.attachmentUrls,

        ...Object.fromEntries(
          daysOfWeek.map((day) => [
            day,
            res.data.location[day] || defaultOperatingHour,
          ])
        ),
      });
    }
  };

  useEffect(() => {
    if (id !== "") getLocationSettingsInfo();
  }, [id]);

  const handleSaveLocationSettings = async () => {
    const updatedData = {
      id: id,
      updatedLocation: {
        ...generalSettings,
        areas: areas,
        ...schedulingSettings,
        ...timesheetSettings,
      },
    };
    setIsLoading(true);
    const res = await api.post("/location/update", updatedData);
    setIsLoading(false);
    if (res.data.success) {
      toast.success(res.data.message);
    } else {
      toast.error(res.data.message);
    }

    getLocationSettingsInfo();

    // console.log(updatedData);
  };

  const archiveLocation = async () => {
    setIsLoading(true);
    const res = await api.post("/location/archive", {
      id: id,
    });

    setIsLoading(false);

    if (res.data.success) {
      toast.success(res.data.message);
      navigate("/locations");
    } else {
      toast.error(res.data.message);
    }
  };

  return (
    <div className="w-full max-w-6xl mx-auto">
      {isLoading && <Spinner />}
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center mt-8 justify-between">
          <div className="flex items-center space-x-4">
            <h1 className="text-2xl font-bold text-gray-900">
              {generalSettings.name}
            </h1>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={() => navigate("/locations")}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded hover:bg-gray-200"
            >
              Back
            </button>
            <button
              onClick={archiveLocation}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded hover:bg-gray-200"
            >
              Archive
            </button>
            <button
              onClick={handleSaveLocationSettings}
              className="px-6 py-2 bg-gray-800 text-white rounded hover:bg-gray-900"
            >
              Save
            </button>
          </div>
        </div>
      </div>

      <div className="flex">
        {/* Sidebar */}
        {/* <div className="w-64 bg-white border-r border-gray-200">
          <nav className="p-4">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`w-full text-left px-4 py-3 rounded-lg mb-2 transition-colors ${
                  activeTab === tab.id
                    ? "bg-gray-800 text-white"
                    : "text-gray-700 hover:bg-gray-100"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div> */}

        {/* Main Content */}
        <div className="flex-1 h-[calc(100vh-200px)]  overflow-y-auto p-8">
          {/* General Tab */}
          {activeTab === "general" && (
            <EditGeneral data={generalSettings} setData={setGeneralSettings} />
          )}

          {/* Scheduling Tab */}
          {activeTab === "scheduling" && (
            <EditScheduling
              data={schedulingSettings}
              setData={setSchedulingSettings}
            />
          )}

          {/* Timesheets Tab */}
          {activeTab === "timesheets" && (
            <EditTimesheet
              data={timesheetSettings}
              setData={setTimesheetSettings}
            />
          )}

          {/* Areas Tab */}
          {activeTab === "areas" && (
            <div className="space-y-6">
              <div className="bg-gray-100 border border-gray-300 rounded-lg p-4">
                <div className="flex items-center space-x-3">
                  <div className="w-5 h-5 bg-gray-600 rounded-full flex items-center justify-center">
                    <span className="text-white text-xs">i</span>
                  </div>
                  <p className="text-gray-700 text-sm">
                    Drag the Areas to change the order of how they are arranged
                    on the Scheduling screen.
                  </p>
                  <button className="text-gray-500 hover:text-gray-700">
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>
              </div>

              <div className="bg-white  p-6">
                <div className="flex items-center space-x-4 mb-6">
                  <input
                    type="text"
                    placeholder="New area name"
                    value={newAreaName}
                    onChange={(e) => setNewAreaName(e.target.value)}
                    className="flex-1 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-transparent"
                  />
                  <button
                    onClick={addArea}
                    className="px-6 py-3 bg-gray-800 text-white rounded-lg hover:bg-gray-900"
                  >
                    Add
                  </button>
                </div>

                <div className="space-y-3">
                  {areas.map((area) => (
                    <div
                      draggable
                      key={area.id}
                      className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
                    >
                      <span className="font-medium text-gray-900">
                        {area.name}
                      </span>
                      <div className="flex items-center space-x-3">
                        <button className="px-4 py-2 text-gray-600 bg-white border border-gray-300 rounded hover:bg-gray-50">
                          Edit
                        </button>
                        <button
                          onClick={() => deleteArea(area.id)}
                          className="px-4 py-2 text-red-600 bg-white border border-red-300 rounded hover:bg-red-50"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Notifications & extensions Tab */}
          {activeTab === "notifications" && (
            <div className="bg-white  px-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-6">
                Notifications & extensions
              </h2>
              <p className="text-gray-600">
                This section contains notification and extension settings for
                the location.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
